export const ReferralViewMode = {
  ACTIVE: 'active',
  ARCHIVE: 'archive',
};

/**
 * Active referrals are incomplete. Archive is the completed ones.
 *
 * @param {object|null|undefined} referral
 * @param {string} viewMode
 * @returns {boolean}
 */
export function referralBelongsInView(referral, viewMode) {
  if (!referral) return false;
  const complete = referral.isComplete === true;
  return viewMode === ReferralViewMode.ARCHIVE ? complete : !complete;
}

/**
 * @param {boolean} isTracked
 * @param {{ from?: string, to?: string, type?: string }|null|undefined} transition
 * @param {string} viewMode
 * @returns {boolean}
 */
export function shouldFetchReferralUpdate(isTracked, transition, viewMode) {
  return isTracked || transitionMayEnterReferralView(transition, viewMode);
}

/**
 * An unseen referral. Creation puts it on the active list. Completion puts it
 * in the archive. Text, type, and date edits do not move it between the two.
 *
 * @param {{ from?: string, to?: string, type?: string }|null|undefined} transition
 * @param {string} viewMode
 * @returns {boolean}
 */
export function transitionMayEnterReferralView(transition, viewMode) {
  if (!transition || transition.to === 'NON_EXISTENT') return false;

  const { from, to, type } = transition;
  if (from === 'NON_EXISTENT' && to === 'READY' && type === 'ALL') {
    return viewMode === ReferralViewMode.ACTIVE;
  }
  if (type === 'COMPLETE') {
    return viewMode === ReferralViewMode.ARCHIVE;
  }
  return false;
}

/**
 * Referral search matches a patient NHS number, a patient name, completed-by,
 * and created-by. The patient is fixed when the referral is created, so a new
 * referral can match a name or NHS search immediately. Completion is what
 * makes completed-by start matching.
 *
 * @param {boolean} isInResults
 * @param {{ from?: string, to?: string, type?: string }|null|undefined} transition
 * @returns {boolean}
 */
export function shouldRefreshReferralSearch(isInResults, transition) {
  if (!transition || transition.to === 'NON_EXISTENT') return false;
  if (isInResults) return true;
  const { from, to, type } = transition;
  return (from === 'NON_EXISTENT' && to === 'READY' && type === 'ALL') || type === 'COMPLETE';
}

/**
 * @param {object[]|null|undefined} previous
 * @param {string} referralId
 * @param {object|null|undefined} fetched
 * @param {boolean} belongs
 * @returns {object[]}
 */
export function mergeFetchedReferral(previous, referralId, fetched, belongs) {
  if (!Array.isArray(previous)) return previous;

  const position = previous.findIndex(referral => referral.referralId === referralId);
  const without = () => previous.filter(referral => referral.referralId !== referralId);

  if (fetched == null || !belongs) {
    return position === -1 ? previous : without();
  }
  if (position === -1) return [...previous, fetched];
  const next = previous.slice();
  next[position] = fetched;
  return next;
}
